import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listProgramsAdmin, deleteProgram, Program } from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";

export default function ProgramsList() {
  const toast = useToast();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  function reload() {
    setLoading(true);
    listProgramsAdmin()
      .then(setPrograms)
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
  }
  useEffect(reload, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function onDelete(p: Program) {
    const has = p._count?.reservationItems ?? 0;
    const msg =
      has > 0
        ? `Program "${p.name}" má ${has} rezervací. Místo smazání se program jen deaktivuje (skryje z rezervačního formuláře). Pokračovat?`
        : `Smazat program "${p.name}"?`;
    if (!confirm(msg)) return;
    try {
      const res = await deleteProgram(p.id);
      toast.show(
        "success",
        res.mode === "deleted" ? "Program smazán." : "Program byl deaktivován (má rezervace)."
      );
      reload();
    } catch (err: any) {
      toast.show("error", err.message ?? "Smazání selhalo.");
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Programy</h1>
          <p className="mt-1 text-slate-600">Co si školky mohou zarezervovat.</p>
        </div>
        <Link to="/admin/programs/new" className="btn-primary">
          + Nový program
        </Link>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-slate-500">Načítám…</div>
        ) : programs.length === 0 ? (
          <div className="card text-center">
            <div className="py-12 text-slate-500">
              <div className="text-4xl">✨</div>
              <div className="mt-4 font-medium">Zatím žádný program</div>
              <div className="mt-1 text-sm">
                <Link to="/admin/programs/new" className="text-obloha-indigo-700 hover:underline">
                  Přidej první program
                </Link>{" "}
                aby si ho školky mohly zarezervovat.
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {programs.map((p) => (
              <ProgramCard key={p.id} p={p} onDelete={() => onDelete(p)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProgramCard({ p, onDelete }: { p: Program; onDelete: () => void }) {
  return (
    <div className="card flex flex-col overflow-hidden p-0">
      <div className="aspect-video w-full bg-slate-100">
        {p.coverImageUrl ? (
          <img src={p.coverImageUrl} alt={p.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-5xl">✨</div>
        )}
      </div>
      <div className="flex-1 p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-900">{p.name}</h3>
          {!p.active && (
            <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
              Neaktivní
            </span>
          )}
        </div>

        <div className="mt-2 text-sm text-slate-600">
          {p.description ? (
            <p className="line-clamp-2">{p.description}</p>
          ) : (
            <p className="italic text-slate-400">Bez popisu</p>
          )}
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
          <InfoItem label="Délka" value={`${p.durationMinutes} min`} />
          <InfoItem label="Max dětí" value={String(p.maxChildrenPerSlot)} />
          <InfoItem label="Cena / dítě" value={`${p.pricePerChildCzk} Kč`} />
          <InfoItem label="Min cena" value={`${p.minPriceCzk} Kč`} />
          <InfoItem label="Montáž" value={`${p.setupMinutes} min`} />
          <InfoItem label="Demontáž" value={`${p.teardownMinutes} min`} />
        </dl>

        {(p.ageGroupFrom != null || p.ageGroupTo != null) && (
          <div className="mt-3 text-xs text-slate-500">
            Pro děti: {p.ageGroupFrom ?? "?"}–{p.ageGroupTo ?? "?"} let
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
        <Link
          to={`/admin/programs/${p.id}`}
          className="text-sm font-medium text-obloha-indigo-700 hover:text-obloha-indigo-900"
        >
          Upravit →
        </Link>
        <button onClick={onDelete} className="text-sm text-red-600 hover:text-red-800">
          Smazat
        </button>
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-slate-400">{label}</dt>
      <dd className="font-medium text-slate-700">{value}</dd>
    </div>
  );
}
